import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { ArticleType, Priority, DividerLineMode } from '../../common/types'
import { ArticleUpsertForm, ArticleViewForm, ArticleBulkUploadForm, ArticleImagesAddForm, ArticlesDeleteForm } from '..'
import { getDefaultDividerLineMode, nullish } from '../ArticleParse'
import { ConfirmationForm } from '../../common'
import type { FormConfig } from '../../common/form/CustomForm'
import { closeModal, openModal } from '../articleActions'
import { modifyLayoutLocked } from '../articleAPI'
import type { Article } from '../types'
import type { LayoutableWithMeta } from './helpers'
import type { UpsertParams } from './utils'
import type { ModalSetting } from './helpers'

type Dispatch = ThunkDispatch<unknown, unknown, UnknownAction>

export interface ArticleModalSetting {
  name: string
  key: string
  form: FormConfig
}

type EditDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  selectedArticleAsArticle: Article | undefined
  enumValues: Record<string, Record<string, string>>
  usedPriorities: Set<string>
  largestOrder: number
  nonHolderArticles: Article[]
  existingFiles: string[]
  handleChangeArticleSubmit: (props: UpsertParams) => void
  dispatch: Dispatch
}

export const createEditModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  selectedArticleAsArticle,
  enumValues,
  usedPriorities,
  largestOrder,
  nonHolderArticles,
  existingFiles,
  handleChangeArticleSubmit,
  dispatch
}: EditDeps): ArticleModalSetting => ({
  name: 'edit',
  key: 'edit-article-modal',
  form: {
    initialValues: {
      layoutableId,
      layoutableType,
      layoutableLockVersion: layoutable?.lockVersion,
      article: selectedArticleAsArticle,
      articleType: selectedArticleAsArticle?.articleType || ArticleType.Flow,
      priority: selectedArticleAsArticle?.priority || Priority.Other,
      columnSpaceExistence: selectedArticleAsArticle?.columnSpaceExistence ?? true,
      dividerLineMode:
        selectedArticleAsArticle?.dividerLineMode || getDefaultDividerLineMode(selectedArticleAsArticle?.articleType),
      filename: selectedArticleAsArticle?.filename || '',
      file: '',
      name: selectedArticleAsArticle?.name || '',
      parentId: selectedArticleAsArticle?.parentId || '',
      relationType: selectedArticleAsArticle?.relationType || '',
      useAuxiliaryLine: selectedArticleAsArticle?.auxiliaryLine !== null,
      auxiliaryLine: selectedArticleAsArticle?.auxiliaryLine,
      rows: selectedArticleAsArticle?.rows || '',
      createArticleWithoutFile:
        selectedArticleAsArticle?.articleType === ArticleType.Flow && !nullish(selectedArticleAsArticle?.name)
    },
    handleSubmit: handleChangeArticleSubmit,
    validationSchema: ArticleUpsertForm.createSchema(layoutable.numberOfRows),
    Component: ArticleUpsertForm,
    props: {
      enumValues,
      usedPriorities,
      largestOrder,
      nonHolderArticles,
      selectedArticle: selectedArticleAsArticle,
      existingFiles,
      title: '記事内容変更',
      layoutable,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '更新する'
    }
  }
})

type ViewDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  selectedArticleAsArticle: Article | undefined
  enumValues: Record<string, Record<string, string>>
  dispatch: Dispatch
}

export const createViewModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  selectedArticleAsArticle,
  enumValues,
  dispatch
}: ViewDeps): ArticleModalSetting => ({
  name: 'view',
  key: 'view-article-modal',
  form: {
    initialValues: {
      layoutableId,
      layoutableType,
      article: selectedArticleAsArticle,
      articleType: selectedArticleAsArticle?.articleType || ArticleType.Flow,
      priority: selectedArticleAsArticle?.priority || Priority.Other,
      columnSpaceExistence: selectedArticleAsArticle?.columnSpaceExistence ?? true,
      dividerLineMode:
        selectedArticleAsArticle?.dividerLineMode || getDefaultDividerLineMode(selectedArticleAsArticle?.articleType),
      filename: selectedArticleAsArticle?.filename || '',
      file: '',
      name: selectedArticleAsArticle?.name || '',
      parentId: selectedArticleAsArticle?.parentId || '',
      relationType: selectedArticleAsArticle?.relationType || '',
      useAuxiliaryLine: selectedArticleAsArticle?.auxiliaryLine !== null,
      auxiliaryLine: selectedArticleAsArticle?.auxiliaryLine,
      rows: selectedArticleAsArticle?.rows || ''
    },
    handleSubmit: () => {},
    Component: ArticleViewForm,
    props: {
      enumValues,
      selectedArticle: selectedArticleAsArticle,
      title: '記事内容詳細',
      layoutable,
      onClickCancel: () => dispatch(closeModal())
    }
  }
})

type UnlockDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  selectedArticleAsArticle: Article | undefined
  enumValues: Record<string, Record<string, string>>
  dispatch: Dispatch
}

export const createUnlockModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  selectedArticleAsArticle,
  enumValues,
  dispatch
}: UnlockDeps): ArticleModalSetting => ({
  name: 'unlock',
  key: 'unlock-article-layout-modal',
  form: {
    initialValues: {
      layoutableId,
      layoutableType,
      lockVersion: layoutable?.lockVersion,
      article: selectedArticleAsArticle
    },
    handleSubmit: ({ article, ...props }: { article: Article; [key: string]: unknown }) => {
      const params = { ...props, article: { id: article.id as string, layoutLocked: false } }
      dispatch(modifyLayoutLocked(params))
    },
    Component: ConfirmationForm,
    props: {
      enumValues,
      selectedArticle: selectedArticleAsArticle,
      title: 'レイアウトロック解除',
      article: selectedArticleAsArticle,
      layoutable,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '解除する',
      ConfirmationBody: ({ article }: { article: Article }) => {
        const articleName = article.filename || article.name || article.id || ''
        return <>「{articleName}」のレイアウトロックを解除しますか？</>
      }
    }
  }
})

type BulkAddDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  holderArticle: Article | undefined
  existingFiles: string[]
  enumValues: Record<string, Record<string, string>>
  handleBulkArticlesSubmit: (props: { layoutableType: string; layoutableId: number; formData: FormData }) => void
  fetching: boolean
  isSelectedLayoutableWritable: boolean
  closeAlert: () => void
  dispatch: Dispatch
}

export const createBulkAddButtonModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  holderArticle,
  existingFiles,
  enumValues,
  handleBulkArticlesSubmit,
  fetching,
  isSelectedLayoutableWritable,
  closeAlert,
  dispatch
}: BulkAddDeps): ModalSetting => ({
  name: 'bulk-add',
  button: {
    handleClick: () => {
      closeAlert()
      dispatch(openModal('bulk-add'))
    },
    disabled: fetching || !isSelectedLayoutableWritable,
    icon: 'folder-plus',
    text: '複数記事追加'
  },
  form: {
    initialValues: { layoutableId, layoutableType, formData: null },
    handleSubmit: handleBulkArticlesSubmit,
    Component: ArticleBulkUploadForm,
    props: {
      enumValues,
      title: '複数記事追加',
      layoutable,
      holderArticle,
      existingFiles,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '追加する',
      layoutableLockVersion: layoutable?.lockVersion
    }
  }
})

type AddDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  existingFiles: string[]
  enumValues: Record<string, Record<string, string>>
  usedPriorities: Set<string>
  largestOrder: number
  insertPositionCandidates: Article[]
  handleAddArticleSubmit: (props: UpsertParams) => void
  fetching: boolean
  isSelectedLayoutableWritable: boolean
  closeAlert: () => void
  alert: unknown
  dispatch: Dispatch
}

export const createAddButtonModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  existingFiles,
  enumValues,
  usedPriorities,
  largestOrder,
  insertPositionCandidates,
  handleAddArticleSubmit,
  fetching,
  isSelectedLayoutableWritable,
  closeAlert,
  alert,
  dispatch
}: AddDeps): ModalSetting => ({
  name: 'add',
  button: {
    handleClick: () => {
      closeAlert()
      dispatch(openModal('add'))
    },
    disabled: fetching || !isSelectedLayoutableWritable,
    icon: 'plus',
    text: '記事追加'
  },
  form: {
    initialValues: {
      layoutableId,
      layoutableType,
      layoutableLockVersion: layoutable?.lockVersion,
      article: null,
      articleType: ArticleType.Flow,
      priority: Priority.Other,
      columnSpaceExistence: true,
      dividerLineMode: DividerLineMode.Both,
      file: '',
      parentId: '',
      relationType: '',
      name: null,
      useAuxiliaryLine: false,
      auxiliaryLine: null,
      insertBeforeArticleId: ''
    },
    handleSubmit: handleAddArticleSubmit,
    validationSchema: ArticleUpsertForm.createSchema(layoutable.numberOfRows),
    Component: ArticleUpsertForm,
    props: {
      enumValues,
      usedPriorities,
      largestOrder,
      insertPositionCandidates,
      title: '新規記事追加',
      layoutable,
      existingFiles,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '追加する',
      alert
    }
  }
})

type ImagesAddDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  holderArticle: Article | undefined
  existingFiles: string[]
  enumValues: Record<string, Record<string, string>>
  handleBulkArticlesSubmit: (props: { layoutableType: string; layoutableId: number; formData: FormData }) => void
  fetching: boolean
  isSelectedLayoutableWritable: boolean
  dispatch: Dispatch
}

export const createImagesAddButtonModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  holderArticle,
  existingFiles,
  enumValues,
  handleBulkArticlesSubmit,
  fetching,
  isSelectedLayoutableWritable,
  dispatch
}: ImagesAddDeps): ModalSetting => ({
  name: 'images-add',
  button: {
    handleClick: () => dispatch(openModal('images-add')),
    disabled: fetching || !isSelectedLayoutableWritable,
    icon: 'images',
    text: '画像追加'
  },
  form: {
    initialValues: {
      layoutableId,
      layoutableType,
      acceptedImageList: [],
      rejectedImageList: [],
      formData: undefined,
      images: undefined
    },
    handleSubmit: handleBulkArticlesSubmit,
    Component: ArticleImagesAddForm,
    props: {
      enumValues,
      title: '画像追加',
      layoutable,
      holderArticle,
      existingFiles,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '追加する',
      layoutableLockVersion: layoutable?.lockVersion
    }
  }
})

type DeleteDeps = {
  layoutableId: number
  layoutableType: string
  layoutable: LayoutableWithMeta
  selectedIds: number[]
  enumValues: Record<string, Record<string, string>>
  handleArticlesDeleteSubmit: (props: {
    layoutableType: string
    layoutableId: number
    layoutableLockVersion: unknown
    selectedIds: number[]
  }) => void
  isSelectedLayoutableWritable: boolean
  dispatch: Dispatch
}

export const createDeleteButtonModalSetting = ({
  layoutableId,
  layoutableType,
  layoutable,
  selectedIds,
  enumValues,
  handleArticlesDeleteSubmit,
  isSelectedLayoutableWritable,
  dispatch
}: DeleteDeps): ModalSetting => ({
  name: 'delete',
  button: {
    handleClick: () => dispatch(openModal('delete')),
    disabled: !isSelectedLayoutableWritable || selectedIds.length === 0,
    variant: 'outline-danger',
    icon: 'trash',
    text: '選択記事削除'
  },
  form: {
    initialValues: { layoutableId, layoutableType, layoutableLockVersion: layoutable?.lockVersion, selectedIds },
    handleSubmit: handleArticlesDeleteSubmit,
    validationSchema: (ArticlesDeleteForm as any).schema,
    Component: ArticlesDeleteForm,
    props: {
      enumValues,
      title: '選択記事削除',
      message: '選択した記事を本当に削除しますか？',
      selectedIds,
      layoutable,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '削除する'
    }
  }
})
